import {differenceInHours, formatISO, parseISO} from "date-fns";
import {formatInTimeZone} from "date-fns-tz";
import {enGB} from "date-fns/locale/en-GB";
import {Series, SeriesOptionsType} from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {SeriesLegendItemClickEventObject} from "highcharts/highcharts";
import {useEffect, useRef, useState} from "react";
import {useDebouncedCallback} from "use-debounce";

import {useCurrentOrPublicUserContext} from "@/components/context/hooks.ts";
import Highcharts from "@/components/Highcharts";
import {CheckDetailsAlert, CheckStatsLocation, LocationResponseTimes} from "@/lib/queries/checks.ts";
import {StandardDateRange} from "@/lib/queries/reports.ts";
import {formatResponseTime} from "@/pages/checks/analysis/overview/utils.ts";

import styles from "./ResponseTimeAreaChart.module.scss";

const ALERT_COLOR = "rgba(206, 5, 0, 0.3)";

const GAP_THRESHOLD_PCT = 0.12;

// HighchartsReact applies option changes with chart.update(), which merges: a key left out in light
// mode keeps its dark value. Both themes therefore set every themed key. The light values are
// Highcharts 13's own defaults (its CSS palette variables), so light charts render exactly as if
// nothing had been passed.
const CHART_THEMES = {
  light: {
    chart: {backgroundColor: "var(--highcharts-background-color)"},
    axis: {
      gridLineColor: "var(--highcharts-neutral-color-10)",
      lineColor: "var(--highcharts-neutral-color-80)",
      tickColor: "var(--highcharts-neutral-color-80)",
    },
    axisLabels: {style: {color: "var(--highcharts-neutral-color-80)"}},
    legend: {
      itemStyle: {color: "var(--highcharts-neutral-color-80)"},
      itemHoverStyle: {color: "var(--highcharts-neutral-color-100)"},
      itemHiddenStyle: {color: "var(--highcharts-neutral-color-60)"},
    },
    tooltip: {
      backgroundColor: "var(--highcharts-background-color)",
      borderColor: "var(--highcharts-neutral-color-5)",
      style: {color: "var(--highcharts-neutral-color-80)"},
    },
  },
  dark: {
    chart: {backgroundColor: "transparent"},
    axis: {
      gridLineColor: styles.chartGridColorDark,
      lineColor: styles.chartGridColorDark,
      tickColor: styles.chartGridColorDark,
    },
    axisLabels: {style: {color: styles.chartAxisColorDark}},
    legend: {
      itemStyle: {color: styles.chartLegendColorDark},
      itemHoverStyle: {color: styles.chartLegendHoverColorDark},
      itemHiddenStyle: {color: styles.chartLegendHiddenColorDark},
    },
    tooltip: {
      backgroundColor: styles.chartTooltipBackgroundDark,
      borderColor: styles.chartTooltipBorderDark,
      style: {color: styles.chartTooltipTextColorDark},
    },
  },
};

interface ResponseTimeAreaChartProps {
  start: Date;
  end: Date;
  responseTimes: LocationResponseTimes[];
  locations: CheckStatsLocation[];
  alerts: CheckDetailsAlert[];
  onLocationClick: (id: string, visible: boolean) => void;
  onRangeChange: (start: Date | null, end: Date | null, fixedRange: StandardDateRange | null) => void;
  showMarkers?: boolean;
  showPlotbandsInLegend?: boolean;
  /** Overrides the viewer's timezone — status pages label their axes in the page's own. */
  timezone?: string;
  /** Repaints the chart chrome for the dark surfaces a status page can render this on. */
  isDark?: boolean;
}

interface customSeriesLegendItemClickEventObject extends SeriesLegendItemClickEventObject {
  target: Series & {options: SeriesOptionsType & {locationId?: string; id: number}};
}

export const ResponseTimeAreaChart = ({
  start,
  end,
  responseTimes,
  alerts,
  locations,
  onLocationClick,
  onRangeChange,
  showMarkers = true,
  showPlotbandsInLegend = false,
  timezone: timezoneProp,
  isDark = false,
}: ResponseTimeAreaChartProps) => {
  const [plotBandsVisible, setPlotBandsVisible] = useState(true);
  const {timezone: viewerTimezone, getShortDateTime} = useCurrentOrPublicUserContext();
  const timezone = timezoneProp ?? viewerTimezone;
  const isDaily = differenceInHours(end, start) <= 24 * 7;
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const debouncedRangeChange = useDebouncedCallback(
    (min: number, max: number) => onRangeChange(new Date(min), new Date(max), null),
    300
  );

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (chart?.xAxis?.[0]) {
      chart.xAxis[0].setExtremes(start.getTime(), end.getTime(), true, false);
    }
  }, [start, end]);

  const plotBands = alerts
    .filter((a) => !a.state_is_up)
    .map((alert) => ({
      color: ALERT_COLOR,
      from: parseISO(alert.created_at).getTime(),
      to: alert.ended_at ? parseISO(alert.ended_at).getTime() : end.getTime(),
    }));

  let minX = start.getTime();
  let maxX = end.getTime();
  let maxY = 0;

  responseTimes.forEach((responses) => {
    responses.data.forEach((item) => {
      const time = parseISO(item.timestamp);
      if (maxY < item.value) {
        maxY = item.value;
      }
      if (maxX < time.getTime()) {
        maxX = time.getTime();
      }
      if (minX > time.getTime()) {
        minX = time.getTime();
      }
    });
  });
  if (maxY === 0) {
    maxY = 1.0;
  }
  if (maxX === 0) {
    maxX = new Date().getTime();
  }
  if (minX === 0) {
    minX = start.getTime();
  }

  const series = responseTimes.map((responses, idx) => ({
    type: "area",
    opacity: 0.4,
    id: idx,
    locationId: responses.id,
    name: locations.find((l) => l.id === responses.id)?.location,
    tooltip: {
      headerFormat: "",
      pointFormatter: function (): string {
        // @ts-expect-error @typescript-eslint/no-this-alias
        // eslint-disable-next-line typescript/no-this-alias
        const params: {
          x: number;
          y: number;
          color: string;
          series: {
            index: number;
            name: string;
          };
        } = this;
        const date = formatISO(new Date(params.x));
        const value = params.y;
        const name = params.series.name;
        const format = `<span style="color:${params.color}">●</span> ${name}: <b>${formatResponseTime(value)}</b><br/>`;
        const formattedDate = getShortDateTime(date);
        return `<span style="font-size: 12px;margin-bottom: 4px">${formattedDate}</span><br/>${format}`;
      },
    },
    data: responses.data.map((p) => [parseISO(p.timestamp).getTime(), p.value]),
  }));

  const isEmpty = series.length === 0;
  const chartTheme = CHART_THEMES[isDark ? "dark" : "light"];
  const options = {
    title: {text: ""},
    credits: {
      enabled: false,
    },
    // Follows the page's own toggle, not the OS scheme (see components/Highcharts.tsx).
    palette: {colorScheme: isDark ? "dark" : "light"},
    chart: {
      ...chartTheme.chart,
      zooming: {
        type: "x",
        resetButton: {
          theme: {
            style: {
              display: "none",
            },
          },
        },
      },
      time: {
        timezone: timezone,
      },
    },
    plotOptions: {
      series: {
        events: {
          legendItemClick: (e: customSeriesLegendItemClickEventObject) => {
            if (!e.target.options.id && e.target.options.id !== 0) {
              if (e.target.name === "Outage Detected") {
                setPlotBandsVisible(!plotBandsVisible);
                return false;
              }
              return;
            }
            if (e.target.options.locationId) {
              onLocationClick(e.target.options.locationId, e.target.visible);
            }
          },
        },
      },
      area: {
        marker: {
          enabled: showMarkers,
        },
        gapSize: GAP_THRESHOLD_PCT * (maxX - minX),
        gapUnit: "value",
      },
    },
    tooltip: {
      ...chartTheme.tooltip,
      shared: true,
      outside: true,
    },
    legend: chartTheme.legend,
    xAxis: {
      ...chartTheme.axis,
      type: "datetime",
      gridLineWidth: 1,
      plotBands: plotBandsVisible ? plotBands : [],
      events: {
        setExtremes: (e: Highcharts.AxisSetExtremesEventObject) => {
          if (e.trigger !== "zoom") {
            return;
          }
          if (e.min && e.max) {
            debouncedRangeChange(e.min, e.max);
            return;
          }

          onRangeChange(null, null, null);
        },
      },
      labels: {
        ...chartTheme.axisLabels,
        formatter: ({value}: {value: number}) => {
          if (isDaily) {
            return formatInTimeZone(new Date(value), timezone, "ha z", {locale: enGB});
          }
          return formatInTimeZone(new Date(value), timezone, "MMM dd", {locale: enGB});
        },
      },
      min: minX,
      max: maxX,
    },
    yAxis: {
      ...chartTheme.axis,
      title: {
        text: undefined,
      },
      labels: {
        ...chartTheme.axisLabels,
        formatter: ({value}: {value: number}) => {
          if (!value) {
            return 0;
          }
          return formatResponseTime(value);
        },
      },
      min: 0,
      max: maxY,
    },
    series: isEmpty
      ? [{name: "No data available"}]
      : [
          ...series,
          ...(showPlotbandsInLegend && plotBands.length > 0
            ? [
                {
                  name: "Outage Detected",
                  color: ALERT_COLOR,
                  lineWidth: 0,
                  marker: {
                    symbol: "square",
                  },
                },
              ]
            : []),
        ],
  };
  return <HighchartsReact ref={chartRef} highcharts={Highcharts} options={options} />;
};
